import { SelectParam, SliderParam, ToggleParam } from '../settings-sections'
import { paramValue } from '@/lib/canvas/shader/params'
import type { ExtractedParam } from '@/lib/canvas/shader/types'

interface ShaderParamControlsProps {
  params: ExtractedParam[]
  values: Record<string, unknown>
  onChange: (name: string, value: number | string | boolean) => void
  /** Full width of the node, not the 336px settings column. */
  embedded?: boolean
}

/** Controls generated from the code's `// @param` annotations. */
export function ShaderParamControls({ params, values, onChange, embedded = false }: ShaderParamControlsProps) {
  if (params.length === 0) {
    return (
      <p className="px-2 py-3 text-xs text-muted-foreground">
        No parameters yet. Declare a uniform, or add a line like{' '}
        <code className="rounded bg-muted px-1 py-0.5 font-mono text-[10px]">// @param float speed 0 5 1</code>
        , and a control appears here.
      </p>
    )
  }

  return (
    <div className={embedded ? 'flex w-full flex-col gap-2 py-1' : 'flex w-[336px] flex-col gap-2 py-1'}>
      {params.map((p) => {
        const label = p.label ?? p.name
        const current = paramValue(p, values)
        switch (p.type) {
          case 'float':
          case 'int': {
            const min = p.min ?? 0
            const max = p.max ?? (p.type === 'int' ? 100 : 1)
            const step = p.type === 'int' ? 1 : (max - min) / 100
            return (
              <SliderParam
                key={p.name}
                label={label}
                value={[Number(current)]}
                onChange={(v) => onChange(p.name, p.type === 'int' ? Math.round(v[0]) : v[0])}
                min={min}
                max={max}
                step={step}
                unit=""
              />
            )
          }
          case 'bool':
            return <ToggleParam key={p.name} label={label} checked={current === true} onCheckedChange={(v) => onChange(p.name, v)} />
          case 'select':
            return (
              <SelectParam
                key={p.name}
                label={label}
                value={String(current)}
                onChange={(v) => onChange(p.name, v)}
                options={(p.options ?? []).map((o) => ({ value: o, label: o }))}
                placeholder="Select"
              />
            )
          case 'color':
            return (
              <label key={p.name} className="flex items-center gap-2 px-2">
                <span className="flex-1 text-sm">{label}</span>
                <input
                  type="color"
                  value={String(current)}
                  onChange={(e) => onChange(p.name, e.target.value)}
                  className="h-7 w-12 cursor-pointer rounded border border-border bg-background"
                />
              </label>
            )
        }
      })}
    </div>
  )
}
